import { useEffect, useState, type CSSProperties } from 'react';
import { useKey } from 'react-use';

interface TimelineProps {
  index: number;
  count: number;
  loading: boolean;
  onChange: (index: number) => void;
}

const PLAYBACK_MS = 300;

/**
 * 50-frame timeline: scrub slider (shows position within the whole scene),
 * prev/next buttons, play/pause, and `,` / `.` keyboard stepping.
 *
 * Arrow keys are intentionally NOT used here — they're reserved for camera
 * rotate/pan in Step 6.
 */
export function Timeline({ index, count, loading, onChange }: TimelineProps) {
  const [playing, setPlaying] = useState(false);
  const clamp = (i: number) => Math.max(0, Math.min(count - 1, i));
  const prev = () => onChange(clamp(index - 1));
  const next = () => onChange(clamp(index + 1));

  // `,` previous / `.` next — the standard video-frame-step keys.
  useKey(',', prev, {}, [index, count]);
  useKey('.', next, {}, [index, count]);

  // Playback advances once the current frame has finished loading, so we don't
  // pile up requests. Network-bound until the FrameCache lands in Step 5.
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      if (loading) return;
      onChange(index >= count - 1 ? 0 : index + 1);
    }, PLAYBACK_MS);
    return () => clearInterval(id);
  }, [playing, loading, index, count, onChange]);

  const atStart = index <= 0;
  const atEnd = index >= count - 1;

  return (
    <div className="timeline-bar" style={bar}>
      <button style={btnStyle(atStart)} onClick={prev} disabled={atStart} title="Previous frame ( , )">
        ⏮
      </button>
      <button style={btnStyle(false)} onClick={() => setPlaying((p) => !p)} title="Play / pause">
        {playing ? '⏸' : '▶'}
      </button>
      <button style={btnStyle(atEnd)} onClick={next} disabled={atEnd} title="Next frame ( . )">
        ⏭
      </button>
      <input
        type="range"
        min={0}
        max={count - 1}
        value={index}
        onChange={(e) => onChange(Number(e.target.value))}
        style={slider}
      />
      <span style={label}>
        frame {index} / {count - 1}
        {loading && <span style={{ opacity: 0.55 }}> · loading…</span>}
      </span>
    </div>
  );
}

const bar: CSSProperties = {
  position: 'fixed',
  left: '50%',
  bottom: 20,
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 14px',
  borderRadius: 10,
  background: 'rgba(12,16,22,0.72)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#e6edf3',
  font: '13px system-ui, sans-serif',
  userSelect: 'none',
};

function btnStyle(disabled: boolean): CSSProperties {
  return {
    width: 30,
    height: 28,
    borderRadius: 6,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'transparent',
    color: '#e6edf3',
    fontSize: 13,
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.35 : 1,
  };
}

const slider: CSSProperties = {
  width: 280,
  accentColor: '#ffcf33',
  cursor: 'pointer',
};

const label: CSSProperties = {
  minWidth: 128,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
